import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LaunchIcon from "@mui/icons-material/Launch";
import AssetService from "../../../apis/AssetService";

const PREVIEW_MAX_HEIGHT = 220;

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) {
    return "";
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const units = ["KB", "MB", "GB", "TB"];
  let size = bytes / 1024;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(size >= 100 ? 0 : size >= 10 ? 1 : 2)} ${units[unitIndex]}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString();
  } catch (error) {
    return String(value);
  }
};

const AssetDetailsSidebar = ({
  asset,
  open,
  onClose = () => {},
  onAssetChange = () => {},
  existingTags = [],
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [editableTags, setEditableTags] = useState(asset?.tags || []);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [tagsError, setTagsError] = useState(null);

  useEffect(() => {
    setEditableTags(asset?.tags || []);
    setTagsError(null);
  }, [asset?.id, asset?.tags]);

  const tagOptions = useMemo(() => {
    const unique = new Set([...(existingTags || []), ...(asset?.tags || [])]);
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [existingTags, asset?.tags]);

  const tagsChanged = useMemo(() => {
    const original = Array.isArray(asset?.tags) ? asset.tags : [];
    if (original.length !== editableTags.length) {
      return true;
    }
    const originalSorted = [...original].sort();
    const editedSorted = [...editableTags].sort();
    return originalSorted.some((value, index) => value !== editedSorted[index]);
  }, [asset?.tags, editableTags]);

  const handleOpenAsset = () => {
    if (!asset?.url) {
      return;
    }
    window.open(asset.url, "_blank", "noopener");
  };

  const handleCopyUrl = async () => {
    if (!asset?.url) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(asset.url);
      } else if (typeof document !== "undefined") {
        const textArea = document.createElement("textarea");
        textArea.value = asset.url;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "absolute";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error("Failed to copy asset URL", error);
    }
  };

  const handleSaveTags = async () => {
    if (!asset?.id || isSavingTags || !tagsChanged) {
      return;
    }

    setIsSavingTags(true);
    setTagsError(null);
    try {
      const response = await AssetService.updateAsset(asset.id, {
        tags: editableTags,
      });
      const updatedAsset = {
        ...asset,
        ...(response || {}),
        tags: Array.isArray(response?.tags) ? response.tags : editableTags,
      };
      onAssetChange?.(updatedAsset);
      setEditableTags(updatedAsset.tags || []);
    } catch (error) {
      console.error("Failed to update asset tags", error);
      setTagsError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to save tags right now."
      );
    } finally {
      setIsSavingTags(false);
    }
  };

  const handleResetTags = () => {
    setEditableTags(asset?.tags || []);
    setTagsError(null);
  };

  const previewType = useMemo(() => {
    const type = asset?.contentType || "";

    if (type.startsWith("image/")) {
      return "image";
    }

    if (type.startsWith("video/")) {
      return "video";
    }

    if (type.startsWith("audio/")) {
      return "audio";
    }

    if (type === "application/pdf") {
      return "pdf";
    }

    if (type.startsWith("text/") || type.includes("json")) {
      return "document";
    }

    return "generic";
  }, [asset?.contentType]);

  const renderPreview = () => {
    if (!asset) {
      return (
        <Typography variant="body2" color="text.secondary">
          Select an asset to view its preview and details.
        </Typography>
      );
    }

    if (!asset.url) {
      return (
        <Typography variant="body2" color="text.secondary">
          This asset does not have a preview available.
        </Typography>
      );
    }

    switch (previewType) {
      case "image":
        return (
          <Box
            component="img"
            src={asset.url}
            alt={asset?.metadata?.alt || asset?.title || asset?.fileName}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        );
      case "video":
        return (
          <Box
            component="video"
            src={asset.url}
            controls
            playsInline
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        );
      case "audio":
        return (
          <Box sx={{ width: "100%" }}>
            <audio controls src={asset.url} style={{ width: "100%" }} />
          </Box>
        );
      case "pdf":
        return (
          <Typography variant="body2" color="text.secondary">
            PDF preview is not available. Open the asset to view it.
          </Typography>
        );
      case "document":
        return (
          <Typography variant="body2" color="text.secondary">
            Text-based preview is not available. Download to review the content.
          </Typography>
        );
      default:
        return (
          <Typography variant="body2" color="text.secondary">
            {asset?.contentType || "Asset"}
          </Typography>
        );
    }
  };

  const sidebarContent = (
    <Box
      role="complementary"
      aria-label="Asset details"
      sx={{
        width: { xs: "100%", lg: 360 },
        boxSizing: "border-box",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack spacing={3} sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Asset details
          </Typography>
          {isDesktop ? (
            <IconButton
              size="small"
              aria-label="Close asset details"
              onClick={onClose}
              disabled={!asset}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : (
            <IconButton size="small" aria-label="Close asset details" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        <Box
          sx={{
            position: "relative",
            height: PREVIEW_MAX_HEIGHT,
            borderRadius: 2,
            bgcolor: "background.default",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {renderPreview()}
        </Box>

        {asset && (
          <Stack spacing={1.5}>
            <Stack spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {asset.title || asset.fileName || "Untitled asset"}
              </Typography>
              {asset.description && (
                <Typography variant="body2" color="text.secondary">
                  {asset.description}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<LaunchIcon fontSize="small" />}
                onClick={handleOpenAsset}
                disabled={!asset?.url}
              >
                Open asset
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={handleCopyUrl}
                disabled={!asset?.url}
              >
                Copy URL
              </Button>
            </Stack>
          </Stack>
        )}

        <Stack spacing={1.5}>
          <Typography variant="subtitle2" color="text.secondary">
            Tags
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            options={tagOptions}
            value={editableTags}
            onChange={(_, value) => setEditableTags(value)}
            renderInput={(params) => <TextField {...params} placeholder="Add tags" />}
            disabled={!asset}
          />
          {tagsError && <Alert severity="error">{tagsError}</Alert>}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="text"
              onClick={handleResetTags}
              disabled={!asset || isSavingTags || !tagsChanged}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveTags}
              disabled={!asset || isSavingTags || !tagsChanged}
            >
              {isSavingTags ? "Saving..." : "Save tags"}
            </Button>
          </Stack>
        </Stack>

        <Divider />

        <Stack spacing={1.5}>
          <Typography variant="subtitle2" color="text.secondary">
            Details
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              File name
            </Typography>
            <Typography variant="body1">{asset?.fileName || "—"}</Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              Content type
            </Typography>
            <Typography variant="body1">{asset?.contentType || "—"}</Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              Size
            </Typography>
            <Typography variant="body1">
              {asset?.size ? formatFileSize(asset.size) : "—"}
            </Typography>
          </Stack>
          {asset?.createdAt && (
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body1">{formatDateTime(asset.createdAt)}</Typography>
            </Stack>
          )}
          {asset?.updatedAt && (
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Updated
              </Typography>
              <Typography variant="body1">{formatDateTime(asset.updatedAt)}</Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );

  const isOpen = Boolean(open && asset);

  if (isDesktop) {
    if (!isOpen) {
      return null;
    }

    return (
      <Paper
        elevation={0}
        sx={{
          width: 360,
          border: 1,
          borderColor: "divider",
          borderRadius: 3,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        {sidebarContent}
      </Paper>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          maxWidth: 360,
        },
      }}
    >
      {sidebarContent}
    </Drawer>
  );
};

export default AssetDetailsSidebar;
